import React, { useState, useEffect, useCallback } from 'react';
import styles from './KnowledgeBaseModule.module.css';
import { Project } from '../../types';
import { knowledgeBaseAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

interface KnowledgeBaseModuleProps {
  project: Project;
}

interface Article {
  _id: string;
  title: string;
  category: string;
  subcategory: string;
  content: string;
  summary?: string;
  tags: string[];
  scope: string;
  status: string;
  featured: boolean;
  metrics: {
    views: number;
    uses: number;
    rating: number;
    ratingCount: number;
  };
  createdBy?: { name: string };
  sourceProject?: { name: string };
  createdAt: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  best_practice: 'Best Practice', template: 'Template', lesson_learned: 'Lesson Learned',
  procedure: 'Procedure', checklist: 'Checklist', case_study: 'Case Study',
  guideline: 'Richtlijn', tip: 'Tip'
};

const CategoryIcon: React.FC<{ category: string; size?: number }> = ({ category, size = 20 }) => {
  const s = size;
  const props = { width: s, height: s, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (category) {
    case 'best_practice': return <svg {...props}><circle cx="12" cy="8" r="6"/><path d="M9 18h6"/><path d="M10 22h4"/><path d="M12 14v4"/></svg>;
    case 'template': return <svg {...props}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>;
    case 'lesson_learned': return <svg {...props}><path d="M12 2a7 7 0 017 7c0 2.38-1.19 4.47-3 5.74V17a2 2 0 01-2 2h-4a2 2 0 01-2-2v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 017-7z"/><line x1="10" y1="22" x2="14" y2="22"/></svg>;
    case 'procedure': return <svg {...props}><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><path d="M9 14l2 2 4-4"/></svg>;
    case 'checklist': return <svg {...props}><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>;
    case 'case_study': return <svg {...props}><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg>;
    case 'guideline': return <svg {...props}><path d="M2 12h20"/><path d="M12 2v20"/><path d="M4.93 4.93l14.14 14.14"/></svg>;
    case 'tip': return <svg {...props}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>;
    default: return <svg {...props}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>;
  }
};

const SUBCATEGORY_LABELS: Record<string, string> = {
  stakeholder_management: 'Stakeholder Management', communicatie: 'Communicatie',
  participatie: 'Participatie', sentiment: 'Sentiment', crisis: 'Crisis',
  vergunningen: 'Vergunningen', gebiedsanalyse: 'Gebiedsanalyse', strategie: 'Strategie',
  media: 'Media', planning: 'Planning', algemeen: 'Algemeen'
};

const StarRating: React.FC<{ score: number }> = ({ score }) => {
  const full = Math.round(score);
  return (
    <span style={{ display: 'inline-flex', gap: 1 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill={i <= full ? '#f59e0b' : 'none'} stroke="#f59e0b" strokeWidth="2">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
      ))}
    </span>
  );
};

export const KnowledgeBaseModule: React.FC<KnowledgeBaseModuleProps> = ({ project }) => {
  const toast = useToast();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'browse' | 'create' | 'detail'>('browse');
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [subcategoryFilter, setSubcategoryFilter] = useState('');
  const [total, setTotal] = useState(0);

  const [formData, setFormData] = useState({
    title: '', category: 'best_practice', subcategory: 'algemeen',
    content: '', summary: '', tags: '', scope: 'company'
  });

  const fetchArticles = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (searchQuery) params.search = searchQuery;
      if (categoryFilter) params.category = categoryFilter;
      if (subcategoryFilter) params.subcategory = subcategoryFilter;

      const data = await knowledgeBaseAPI.getAll(params);
      setArticles(data.articles || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch articles:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, categoryFilter, subcategoryFilter]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const handleCreate = async () => {
    try {
      const data = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        sourceProject: project.id
      };
      await knowledgeBaseAPI.create(data);
      toast.success('Artikel aangemaakt');
      setView('browse');
      resetForm();
      fetchArticles();
    } catch (error) {
      toast.error('Fout bij aanmaken');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await knowledgeBaseAPI.delete(id);
      toast.success('Artikel verwijderd');
      fetchArticles();
      if (selectedArticle?._id === id) setView('browse');
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleRate = async (id: string, score: number) => {
    try {
      await knowledgeBaseAPI.rate(id, score);
      toast.success('Beoordeling opgeslagen');
      if (selectedArticle?._id === id) {
        const updated = await knowledgeBaseAPI.getById(id);
        setSelectedArticle(updated);
      }
    } catch (error) {
      toast.error('Fout bij beoordelen');
    }
  };

  const viewArticle = async (article: Article) => {
    try {
      const full = await knowledgeBaseAPI.getById(article._id);
      setSelectedArticle(full);
      setView('detail');
    } catch (error) {
      toast.error('Fout bij laden');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', category: 'best_practice', subcategory: 'algemeen', content: '', summary: '', tags: '', scope: 'company' });
  };

  const featuredArticles = articles.filter(a => a.featured);
  const regularArticles = articles.filter(a => !a.featured);

  if (loading && articles.length === 0) {
    return <div className={styles.container}><div className={styles.loading}>Laden...</div></div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Kennisbank</h1>
          <p className={styles.subtitle}>Best practices, templates en lessons learned</p>
        </div>
        {view === 'browse' ? (
          <button className={styles.primaryButton} onClick={() => setView('create')}>+ Nieuw Artikel</button>
        ) : (
          <button className={styles.secondaryButton} onClick={() => { setView('browse'); setSelectedArticle(null); }}>Terug naar overzicht</button>
        )}
      </div>

      {view === 'browse' && (
        <>
          {/* Search & filters */}
          <div className={styles.searchBar}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input type="text" placeholder="Zoek in de kennisbank..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          </div>

          <div className={styles.filters}>
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className={styles.filterSelect}>
              <option value="">Alle categorieen</option>
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select>
            <select value={subcategoryFilter} onChange={e => setSubcategoryFilter(e.target.value)} className={styles.filterSelect}>
              <option value="">Alle onderwerpen</option>
              {Object.entries(SUBCATEGORY_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select>
            <span className={styles.resultCount}>{total} artikelen gevonden</span>
          </div>

          {/* Featured articles */}
          {featuredArticles.length > 0 && !searchQuery && !categoryFilter && (
            <div className={styles.featuredSection}>
              <h3 className={styles.sectionTitle}>Uitgelicht</h3>
              <div className={styles.featuredGrid}>
                {featuredArticles.slice(0, 3).map(article => (
                  <div key={article._id} className={styles.featuredCard} onClick={() => viewArticle(article)}>
                    <span className={styles.featuredIcon}><CategoryIcon category={article.category} size={28} /></span>
                    <h4>{article.title}</h4>
                    <p>{article.summary || article.content.substring(0, 120) + '...'}</p>
                    <div className={styles.featuredMeta}>
                      <span>{CATEGORY_LABELS[article.category]}</span>
                      {article.metrics.rating > 0 && <span>{article.metrics.rating.toFixed(1)} ({article.metrics.ratingCount})</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Articles list */}
          {(searchQuery || categoryFilter ? articles : regularArticles).length === 0 ? (
            <div className={styles.emptyState}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg>
              <h3>Geen artikelen gevonden</h3>
              <p>{searchQuery ? 'Pas uw zoekterm of filters aan.' : 'Voeg kennis toe aan de kennisbank.'}</p>
            </div>
          ) : (
            <div className={styles.articlesList}>
              {(searchQuery || categoryFilter ? articles : regularArticles).map(article => (
                <div key={article._id} className={styles.articleCard} onClick={() => viewArticle(article)}>
                  <div className={styles.articleIcon}><CategoryIcon category={article.category} size={24} /></div>
                  <div className={styles.articleContent}>
                    <h4>{article.title}</h4>
                    <p>{article.summary || article.content.substring(0, 150) + '...'}</p>
                    <div className={styles.articleMeta}>
                      <span className={styles.categoryBadge}>{CATEGORY_LABELS[article.category]}</span>
                      <span className={styles.subcategoryBadge}>{SUBCATEGORY_LABELS[article.subcategory]}</span>
                      {article.tags.slice(0, 3).map(tag => <span key={tag} className={styles.tag}>{tag}</span>)}
                    </div>
                  </div>
                  <div className={styles.articleStats}>
                    <span title="Bekeken">{article.metrics.views} views</span>
                    {article.metrics.rating > 0 && (
                      <span className={styles.rating}><StarRating score={article.metrics.rating} /> {article.metrics.rating.toFixed(1)}</span>
                    )}
                    <button className={styles.deleteSmall} onClick={e => { e.stopPropagation(); handleDelete(article._id); }}>Verwijder</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {view === 'create' && (
        <div className={styles.createForm}>
          <h2>Nieuw Kennisartikel</h2>
          <div className={styles.formGroup}>
            <label>Titel *</label>
            <input type="text" value={formData.title} onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))} placeholder="Bijv. Stakeholder mapping bij grote infra-projecten" />
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Categorie</label>
              <select value={formData.category} onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}>
                {Object.entries(CATEGORY_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>Onderwerp</label>
              <select value={formData.subcategory} onChange={e => setFormData(prev => ({ ...prev, subcategory: e.target.value }))}>
                {Object.entries(SUBCATEGORY_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>Scope</label>
              <select value={formData.scope} onChange={e => setFormData(prev => ({ ...prev, scope: e.target.value }))}>
                <option value="company">Bedrijf</option>
                <option value="global">Globaal (voor iedereen)</option>
                <option value="project">Alleen dit project</option>
              </select>
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>Samenvatting</label>
            <textarea value={formData.summary} onChange={e => setFormData(prev => ({ ...prev, summary: e.target.value }))} rows={2} placeholder="Korte samenvatting van het artikel" />
          </div>
          <div className={styles.formGroup}>
            <label>Inhoud *</label>
            <textarea value={formData.content} onChange={e => setFormData(prev => ({ ...prev, content: e.target.value }))} rows={12} placeholder="Schrijf het artikel... Markdown wordt ondersteund." className={styles.contentEditor} />
          </div>
          <div className={styles.formGroup}>
            <label>Tags (kommagescheiden)</label>
            <input type="text" value={formData.tags} onChange={e => setFormData(prev => ({ ...prev, tags: e.target.value }))} placeholder="stakeholders, communicatie, infra" />
          </div>
          <div className={styles.formActions}>
            <button className={styles.secondaryButton} onClick={() => { setView('browse'); resetForm(); }}>Annuleren</button>
            <button className={styles.primaryButton} onClick={handleCreate} disabled={!formData.title || !formData.content}>Publiceren</button>
          </div>
        </div>
      )}

      {view === 'detail' && selectedArticle && (
        <div className={styles.detailView}>
          <div className={styles.detailHeader}>
            <span className={styles.detailIcon}><CategoryIcon category={selectedArticle.category} size={36} /></span>
            <div>
              <h2>{selectedArticle.title}</h2>
              <div className={styles.detailMeta}>
                <span className={styles.categoryBadge}>{CATEGORY_LABELS[selectedArticle.category]}</span>
                <span className={styles.subcategoryBadge}>{SUBCATEGORY_LABELS[selectedArticle.subcategory]}</span>
                <span className={styles.metaText}>
                  {selectedArticle.createdBy?.name && `door ${selectedArticle.createdBy.name}`}
                  {' | '}{new Date(selectedArticle.createdAt).toLocaleDateString('nl-NL')}
                  {' | '}{selectedArticle.metrics.views} views
                </span>
              </div>
            </div>
          </div>

          {selectedArticle.summary && (
            <div className={styles.summaryBlock}>
              <strong>Samenvatting:</strong> {selectedArticle.summary}
            </div>
          )}

          <div className={styles.contentBlock}>
            {selectedArticle.content.split('\n').map((paragraph, idx) => (
              paragraph.trim() ? <p key={idx}>{paragraph}</p> : <br key={idx} />
            ))}
          </div>

          {selectedArticle.tags.length > 0 && (
            <div className={styles.tagsSection}>
              {selectedArticle.tags.map(tag => <span key={tag} className={styles.tag}>{tag}</span>)}
            </div>
          )}

          {/* Rating */}
          <div className={styles.ratingSection}>
            <h4>Was dit artikel nuttig?</h4>
            <div className={styles.ratingStars}>
              {[1, 2, 3, 4, 5].map(score => (
                <button key={score} className={styles.starButton} onClick={() => handleRate(selectedArticle._id, score)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                </button>
              ))}
            </div>
            {selectedArticle.metrics.rating > 0 && (
              <span className={styles.currentRating}>
                Gemiddelde: {selectedArticle.metrics.rating.toFixed(1)} ({selectedArticle.metrics.ratingCount} beoordelingen)
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
